import { useQuery, UseQueryResult } from 'react-query';
import { useDispatch } from 'react-redux';
import { axiosCustomInstance, axiosInstance } from './axios';
import { types } from '../../modules/pendings';
import { RETRY_QUERY } from '../../../utils/constants';
import HtmlUtils from '../../../utils/htmlUtils';
import { ToastWithError } from '../../../navigation/routes/toastProvider';
import { ApiType } from '../../apiTypes';
import { asyncStorageApi, KEY_LOCAL_STORAGE_NATIVE_OFFLINE } from '../../../localStorageAPI';
import featureFlag from '../../../config/featureFlag';
import configs from '../../../config';

export interface QueryInfos {
    name: string;
    path: string;
    type: string; // UNIQUE
    params?: any;
    // Params sent with the request but excluded from the query key, evaluated at fetch time.
    // Use it for values that change on every render (time windows...) to keep the key stable.
    dynamicParams?: () => Record<string, unknown>;
    method?: string;
    apiType?: ApiType;
    responseType?: 'arraybuffer' | 'document' | 'json' | 'text' | 'stream' | 'blob';
}

// https://react-query.tanstack.com/reference/useQuery
export interface Options<K> {
    variables?: K;
    select?: (data: any) => any; // selectors to return only a piece of data or data transformation
    enabled?: boolean; // Set this to false to disable this query from automatically running.
    staleTime?: number; // Duration in ms when the data is consider fresh and won't be fetch again except for a refetch
    cacheTime?: number; // Cache Time
    timeout?: number; // custom Timeout
    retry?: number; // number of retry
    retryDelay?: number; // number in ms before next retry
    refetchInterval?: number | false;
    refetchOnWindowFocus?: boolean;
    notifyOnChangeProps?: 'tracked' | (keyof UseQueryResult)[]; // limit the re-renders triggered by the query
    onSuccess?: (data: any, variables?, context?) => any;
    onError?: (error: Error, variables?, context?) => any;
}

const axiosInstances = { [ApiType.PRIVAPI]: axiosInstance, [ApiType.ID]: axiosCustomInstance };

const axiosFetcher = async (queryInfos: QueryInfos): Promise<any> => {
    const {
        path,
        params: staticParams,
        dynamicParams,
        method = 'get',
        apiType = ApiType.PRIVAPI,
        responseType,
    }: QueryInfos = queryInfos;
    const params = { ...staticParams, ...dynamicParams?.() };
    console.log('QUERY - METHOD', method, 'URL:', path, 'PARAMS:', params);

    const isOffline = asyncStorageApi.restoreDataNoLink(KEY_LOCAL_STORAGE_NATIVE_OFFLINE);
    if (isOffline) {
        return Promise.reject(new Error('Offline'));
    }

    try {
        const { data, headers } = await axiosInstances[apiType][method](path, {
            params,
            ...(responseType && { responseType }),
        });
        if (responseType === 'blob') {
            HtmlUtils.createDownloadAction(data, headers);
        }

        console.log('QUERY - RESPONSE', method, 'URL:', path, 'RESPONSE:', data);
        return data;
    } catch (error) {
        // Log the error for debugging
        console.error('QUERY - ERROR', method, 'URL:', path, 'ERROR:', error);

        // Handle specific error scenarios if needed
        if (error.response) {
            // Server responded with a status code outside of the 2xx range
            const errorResponseData = { ...error.response.data };
            // If in staging or production, stringify details to be able to read it in Sentry
            if (
                !configs?.appConfig?.isDev &&
                errorResponseData?.details &&
                (typeof errorResponseData.details === 'object' || Array.isArray(errorResponseData.details))
            ) {
                errorResponseData.details = JSON.stringify(errorResponseData.details);
            }
            console.error(
                'Server responded with an error:',
                error.response.status,
                'Response data:',
                errorResponseData,
            );

            // Optionally, handle specific status codes
            if (error.response.status === 404) {
                console.error('Resource not found');
            } else if (error.response.status === 500) {
                console.error('Internal server error');
            }
            // You can add more status code checks as needed
        } else if (error.request) {
            // Request was made but no response was received
            console.error('No response received:', error.request);
        } else {
            // Something else caused the error
            console.error('Error setting up the request:', error.message);
        }

        // Optionally, throw the error to be handled by the caller
        throw error;
    }
};

export function useMyQuery<T, K = any>(
    query: (variables: K) => QueryInfos,
    options: Options<K> = { variables: undefined },
): UseQueryResult<T, Error> {
    const dispatch = useDispatch();
    const { variables, onSuccess, onError, timeout, ...reactQueryOptions } = options;

    const isOffline = asyncStorageApi.restoreDataNoLink(KEY_LOCAL_STORAGE_NATIVE_OFFLINE);

    const queryInfos: QueryInfos = query(variables);
    const { name, params, type }: QueryInfos = queryInfos;
    const queryKey = [];
    if (name) {
        queryKey.push(name);
    }
    if (Object.keys(params)?.length > 0) {
        queryKey.push(params);
    }

    return useQuery<T, Error>(
        queryKey,
        () => {
            if (featureFlag.pending.active) {
                dispatch({ type: types.START_PENDING_OPERATION, payload: { type } });
            }
            if (timeout) {
                return Promise.race([
                    axiosFetcher(queryInfos),
                    new Promise((_, reject) => setTimeout(() => reject(new Error('Request timed out')), timeout)),
                ]);
            }
            return axiosFetcher(queryInfos);
        },
        {
            ...reactQueryOptions,
            ...(isOffline && { retry: 0 }),
            onSuccess: (data) => {
                if (onSuccess) {
                    onSuccess(data, params);
                }
            },
            onError: (error: Error) => {
                if (onError) {
                    onError(error);
                } else if (!isOffline) {
                    ToastWithError(error);
                }
            },
            onSettled: () => {
                if (featureFlag.pending.active) {
                    dispatch({ type: types.STOP_PENDING_OPERATION, payload: { type } });
                }
            },
            retry: (failureCount) => {
                const nbRetry = reactQueryOptions.retry ?? RETRY_QUERY;
                if (featureFlag.pending.active) {
                    dispatch({ type: types.STOP_PENDING_OPERATION, payload: { type } });
                }
                return failureCount < nbRetry;
            },
            // useErrorBoundary: !configs?.appConfig?.isDev, // we want toast msg on dev & silent err on othen env
        },
    );
}

export function createQuery(
    name: string,
    path: string,
    variables: Record<string, unknown>,
    method = 'get',
    apiType = ApiType.PRIVAPI,
): Omit<QueryInfos, 'type'> {
    return { name, path, params: { ...variables }, method, apiType };
}
